import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles, Home as HomeIcon, Shirt } from 'lucide-react';

const HomePage = () => {
  const services = [
    {
      icon: <Shirt className="h-8 w-8" />,
      title: 'Personal Styling',
      description: 'Transform your wardrobe with expert closet clean-outs and capsule wardrobe curation.',
      link: '/personal-styling',
      image: 'https://customer-assets.emergentagent.com/job_breeze-styling/artifacts/x9r1qxul_image.png',
    },
    {
      icon: <HomeIcon className="h-8 w-8" />,
      title: 'Interior Design',
      description: 'Create serene, functional spaces that reflect your coastal California lifestyle.',
      link: '/interior-design',
      image: 'https://customer-assets.emergentagent.com/job_breeze-styling/artifacts/g85ebatp_image.png',
    },
  ];

  const testimonials = [
    {
      quote: "Working with Maison LouLou completely transformed how I approach getting dressed each morning. My closet finally makes sense!",
      author: "Sarah M.",
      location: "La Jolla, CA"
    },
    {
      quote: "The interior design consultation exceeded all my expectations. My home now feels like a true coastal retreat.",
      author: "Jennifer K.",
      location: "Del Mar, CA"
    },
    {
      quote: "I never knew I could feel this confident in my own wardrobe. The complete wardrobe overhaul was life changing.",
      author: "Danielle C.",
      location: "Seattle, WA"
    }
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1554496609-3599bdd89627?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
            alt="San Diego coastal view"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/60" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <p className="font-sans text-sm uppercase tracking-[0.3em] text-[#D4A373] mb-6 animate-fade-in-up">
            San Diego, California
          </p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-[#2C3E50] leading-none mb-8 animate-fade-in-up animation-delay-200">
            Elevate Your
            <br />
            <em className="italic">Everyday</em>
          </h1>
          <p className="font-sans text-lg md:text-xl text-[#57534E] max-w-2xl mx-auto mb-12 animate-fade-in-up animation-delay-400">
            Personal styling and interior design for the modern coastal lifestyle. 
            Creating spaces and wardrobes that inspire confidence and calm.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-600">
            <Link to="/personal-styling">
              <Button
                data-testid="hero-styling-btn"
                className="rounded-full bg-[#2C3E50] hover:bg-[#1a252f] text-white px-8 py-6 text-base transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                Explore Styling Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/interior-design">
              <Button
                data-testid="hero-interior-btn"
                variant="outline"
                className="rounded-full border-[#2C3E50] text-[#2C3E50] hover:bg-[#2C3E50] hover:text-white px-8 py-6 text-base transition-all duration-300"
              >
                View Interior Design
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#2C3E50]/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-[#2C3E50]/30 rounded-full" />
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-6">
                Our Philosophy
              </p>
              <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50] mb-8 leading-tight">
                Simplicity is the ultimate sophistication
              </h2>
              <p className="font-sans text-[#57534E] text-lg leading-relaxed mb-6">
                At Maison LouLou, we believe that your wardrobe and home should bring you joy, 
                not overwhelm. Our approach combines California's laid-back elegance with 
                intentional design, creating spaces and styles that are both beautiful and functional.
              </p>
              <p className="font-sans text-[#57534E] text-lg leading-relaxed">
                Whether you're seeking a complete closet transformation or looking to refresh 
                your living space, we're here to guide you toward a more curated, confident lifestyle.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_breeze-styling/artifacts/qowp8y5b_image.png"
                  alt="Curated wardrobe"
                  className="w-full h-full object-cover img-zoom"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-[#D4A373]/10 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-4">
              What We Offer
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50]">
              Our Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                data-testid={`service-card-${index}`}
                className="group block"
              >
                <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-8 md:p-10">
                    <div className="text-[#D4A373] mb-4">{service.icon}</div>
                    <h3 className="font-serif text-2xl md:text-3xl text-[#2C3E50] mb-4">
                      {service.title}
                    </h3>
                    <p className="font-sans text-[#57534E] mb-6">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center text-[#2C3E50] font-sans text-sm group-hover:text-[#D4A373] transition-colors">
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-2 transition-transform" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-4">
              Client Love
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50]">
              What They're Saying
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                data-testid={`testimonial-${index}`}
                className="bg-[#F9F7F2] p-8 md:p-10 rounded-2xl"
              >
                <Sparkles className="h-8 w-8 text-[#D4A373] mb-6" />
                <p className="font-sans text-[#57534E] text-lg leading-relaxed mb-8 italic">
                  "{testimonial.quote}"
                </p>
                <div>
                  <p className="font-serif text-[#2C3E50] text-lg">
                    {testimonial.author}
                  </p>
                  <p className="font-sans text-[#D4A373] text-sm">
                    {testimonial.location}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/testimonials">
              <Button
                data-testid="view-testimonials-btn"
                variant="outline"
                className="rounded-full border-[#2C3E50] text-[#2C3E50] hover:bg-[#2C3E50] hover:text-white px-8 py-6"
              >
                View All Testimonials
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#2C3E50] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            Ready to Transform Your Space?
          </h2>
          <p className="font-sans text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Let's create a wardrobe and home that truly reflects who you are. 
            Book a consultation today.
          </p>
          <Link to="/contact">
            <Button
              data-testid="cta-contact-btn"
              className="rounded-full bg-[#D4A373] hover:bg-[#c29260] text-white px-10 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              Get in Touch
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
